import { DashboardMetrics } from "../lib";

import { StatCard } from "./stat-card";

interface InsightStatCardsProps {
  metrics: DashboardMetrics;
  loading?: boolean;
}

export const InsightStatCards = ({
  metrics,
  loading,
}: InsightStatCardsProps) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
    <StatCard
      title="Total Flags"
      value={metrics.totalFlags}
      caption={`${metrics.totalGates} gates across all flags`}
      accent="brand"
      loading={loading}
    />
    <StatCard
      title="Globally Enabled"
      value={metrics.globallyEnabled}
      caption={
        metrics.globallyEnabledWithExceptions
          ? `${metrics.globallyEnabledWithExceptions} with explicit disables`
          : "On for everyone"
      }
      accent="success"
      loading={loading}
    />
    <StatCard
      title="Partially Enabled"
      value={metrics.partiallyEnabled}
      caption="Enabled for specific orgs/groups only"
      accent="info"
      loading={loading}
    />
    <StatCard
      title="Off"
      value={metrics.off}
      caption="No enabled gates anywhere"
      accent="muted"
      loading={loading}
    />
    <StatCard
      title="User Overrides"
      value={metrics.withUserOverrides}
      caption="Flags with user-level gates"
      accent="warning"
      loading={loading}
    />
    <StatCard
      title="Undocumented"
      value={metrics.undocumented}
      caption="Missing from the description catalog"
      accent={metrics.undocumented ? "destructive" : "muted"}
      loading={loading}
    />
  </div>
);

export const StatusDistributionBar = ({
  metrics,
  loading,
}: InsightStatCardsProps) => {
  if (loading || !metrics.totalFlags) {
    return null;
  }

  const segments = [
    {
      label: "Global",
      count: metrics.globallyEnabled,
      className: "bg-success",
    },
    {
      label: "Partial",
      count: metrics.partiallyEnabled,
      className: "bg-info",
    },
    { label: "Off", count: metrics.off, className: "bg-muted-foreground" },
  ].filter((segment) => segment.count > 0);

  return (
    <div className="space-y-2">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
        {segments.map((segment) => (
          <div
            key={segment.label}
            className={segment.className}
            style={{
              width: `${(segment.count / metrics.totalFlags) * 100}%`,
            }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        {segments.map((segment) => (
          <div key={segment.label} className="flex items-center gap-1.5">
            <span
              className={`inline-block h-2 w-2 rounded-full ${segment.className}`}
            />
            {segment.label}: {segment.count} (
            {Math.round((segment.count / metrics.totalFlags) * 100)}%)
          </div>
        ))}
      </div>
    </div>
  );
};
